import React, { useState, useEffect, useContext } from "react";
import {
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Tooltip
} from "@material-ui/core";
import {
  Visibility as VisibilityIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon
} from "@material-ui/icons";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  filterSection: {
    marginBottom: theme.spacing(3),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  statusChip: {
    minWidth: 100,
  },
  transcriptBox: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: 4,
    maxHeight: 400,
    overflowY: "auto",
    ...theme.scrollbarStyles,
    whiteSpace: "pre-wrap",
    fontFamily: "monospace",
  },
  detailRow: {
    marginBottom: theme.spacing(1),
  },
}));

const statusColors = {
  "completed": "primary",
  "in-progress": "secondary",
  "failed": "default",
  "busy": "default",
  "no-answer": "default",
  "canceled": "default",
  "queued": "default",
  "ringing": "secondary",
};

const statusLabels = {
  "completed": "Completada",
  "in-progress": "En Progreso",
  "failed": "Fallida",
  "busy": "Ocupado",
  "no-answer": "Sin Respuesta",
  "canceled": "Cancelada",
  "queued": "En Cola",
  "ringing": "Timbrando",
};

const AICallHistory = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");

  // Detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);

  useEffect(() => {
    loadCalls();
  }, [page, rowsPerPage]);

  const loadCalls = async () => {
    try {
      setLoading(true);

      const params = {
        companyId: user.companyId,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };

      if (statusFilter) params.status = statusFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const { data } = await api.get("/api/twilio/calls", { params });

      setCalls(data.calls || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error("Error loading calls:", error);
      toast.error("Error al cargar el historial de llamadas");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = async (call) => {
    try {
      // Fetch full call details including transcript
      const { data } = await api.get(`/api/twilio/calls/${call.callSid}`);
      setSelectedCall(data);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error("Error loading call details:", error);
      toast.error("Error al cargar detalles de la llamada");
    }
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedCall(null);
  };

  const handleExportTranscript = () => {
    if (!selectedCall || !selectedCall.transcript) {
      toast.error("No hay transcripción disponible");
      return;
    }

    const blob = new Blob([selectedCall.transcript], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transcript_${selectedCall.callSid}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("Transcripción descargada");
  };

  const handleApplyFilters = () => {
    setPage(0);
    loadCalls();
  };

  const handleClearFilters = () => {
    setStatusFilter("");
    setStartDate("");
    setEndDate("");
    setPhoneFilter("");
    setPage(0);
    loadCalls();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "-";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatCost = (cost) => {
    if (!cost) return "-";
    return `$${cost.toFixed(4)}`;
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Historial de Llamadas con IA</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={loadCalls}
            startIcon={<RefreshIcon />}
          >
            Actualizar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        {/* Filters */}
        <Paper className={classes.filterSection} elevation={1} style={{ padding: 16 }}>
          <Typography variant="h6" gutterBottom>
            Filtros
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Estado</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Estado"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="completed">Completada</MenuItem>
                  <MenuItem value="in-progress">En Progreso</MenuItem>
                  <MenuItem value="failed">Fallida</MenuItem>
                  <MenuItem value="busy">Ocupado</MenuItem>
                  <MenuItem value="no-answer">Sin Respuesta</MenuItem>
                  <MenuItem value="canceled">Cancelada</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                variant="outlined"
                label="Fecha Inicio"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                variant="outlined"
                label="Fecha Fin"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleApplyFilters}
                  fullWidth
                >
                  Aplicar
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  fullWidth
                >
                  Limpiar
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
        <TableContainer className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Fecha/Hora</TableCell>
                <TableCell>Número</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Duración</TableCell>
                <TableCell>Costo</TableCell>
                <TableCell>Modelo</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Cargando...
                  </TableCell>
                </TableRow>
              ) : calls.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No hay llamadas registradas
                  </TableCell>
                </TableRow>
              ) : (
                calls.map((call) => (
                  <TableRow key={call.id} hover>
                    <TableCell>
                      {format(parseISO(call.startTime), "dd/MM/yyyy HH:mm", { locale: es })}
                    </TableCell>
                    <TableCell>{call.phoneNumber}</TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabels[call.status] || call.status}
                        color={statusColors[call.status] || "default"}
                        size="small"
                        className={classes.statusChip}
                      />
                    </TableCell>
                    <TableCell>{formatDuration(call.duration)}</TableCell>
                    <TableCell>{formatCost(call.cost)}</TableCell>
                    <TableCell>{call.model || "-"}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver Detalles">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(call)}
                          color="primary"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      {call.recordingUrl && (
                        <Tooltip title="Reproducir Grabación">
                          <IconButton
                            size="small"
                            onClick={() => window.open(call.recordingUrl, "_blank")}
                            color="secondary"
                          >
                            <PlayIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />

        {/* Detail Dialog */}
        <Dialog
          open={detailDialogOpen}
          onClose={handleCloseDetailDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Detalles de la Llamada
            {selectedCall && (
              <Chip
                label={statusLabels[selectedCall.status] || selectedCall.status}
                color={statusColors[selectedCall.status] || "default"}
                size="small"
                style={{ marginLeft: 16 }}
              />
            )}
          </DialogTitle>
          <DialogContent dividers>
            {selectedCall && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Call SID
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      {selectedCall.callSid}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Número de Teléfono
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      {selectedCall.phoneNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Fecha/Hora Inicio
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      {format(parseISO(selectedCall.startTime), "dd/MM/yyyy HH:mm:ss", {
                        locale: es,
                      })}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Duración
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      {formatDuration(selectedCall.duration)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Modelo IA
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      {selectedCall.model || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Voice ID
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      {selectedCall.voiceId || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Costo
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      {formatCost(selectedCall.cost)}
                    </Typography>
                  </Grid>
                </Grid>

                {selectedCall.transcript && (
                  <>
                    <Box mt={3} mb={1} display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6">Transcripción</Typography>
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportTranscript}
                      >
                        Descargar
                      </Button>
                    </Box>
                    <Box className={classes.transcriptBox}>
                      <Typography variant="body2" component="pre">
                        {selectedCall.transcript}
                      </Typography>
                    </Box>
                  </>
                )}

                {!selectedCall.transcript && (
                  <Box mt={3}>
                    <Typography variant="body2" color="textSecondary" align="center">
                      No hay transcripción disponible para esta llamada
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetailDialog} color="primary">
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </MainContainer>
  );
};

export default AICallHistory;
